import React, { useState } from 'react';
import { Card, Table, Button, Badge } from 'react-bootstrap';
import moment from 'moment-timezone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMagnifyingGlass,
  faArrowUp,
  faArrowDown,
} from '@fortawesome/free-solid-svg-icons';

import TranscriptEditor from './TranscriptEditor';

// RingSense insights are stored in transcriptContent. Pull the useful bits
// defensively — video/legacy rows won't carry all of them.
const getMeta = (row) => {
  const content = row.transcriptContent || {};
  const insights = content.insights || {};
  return {
    direction: content.callDirection || '',
    durationMs: content.recordingDurationMs || 0,
    summary: insights.Summary?.[0]?.value || content.summary || '',
    sentiment: insights.Sentiment?.[0]?.value || '',
  };
};

const formatDuration = (ms) => {
  if (!ms) return '—';
  const totalSec = Math.round(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const truncate = (text, n = 90) =>
  text && text.length > n ? `${text.slice(0, n)}…` : text || '';

const sentimentVariant = (s) => {
  switch (String(s).toLowerCase()) {
    case 'positive':
      return 'success';
    case 'negative':
      return 'danger';
    case 'neutral':
      return 'secondary';
    default:
      return null;
  }
};

// Colored diagonal arrows: inbound (green, ↙) vs outbound (blue, ↗).
const directionIcon = (direction) => {
  const d = String(direction).toLowerCase();
  if (d === 'inbound') {
    return (
      <FontAwesomeIcon
        icon={faArrowDown}
        transform={{ rotate: 45 }}
        className="text-success"
        title="Inbound"
      />
    );
  }
  if (d === 'outbound') {
    return (
      <FontAwesomeIcon
        icon={faArrowUp}
        transform={{ rotate: 45 }}
        className="text-primary"
        title="Outbound"
      />
    );
  }
  return '—';
};

const TranscriptsTab = ({ claim, transcripts, questionnaireMatrix }) => {
  const [transcript, setTranscript] = useState(null);

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          {!transcripts || transcripts.length === 0 ? (
            <p className="text-center lead">No transcripts are available.</p>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th className="text-center">Direction</th>
                  <th>Duration</th>
                  <th>Summary</th>
                  <th>Sentiment</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {transcripts.map((row, idx) => {
                  const meta = getMeta(row);
                  const variant = sentimentVariant(meta.sentiment);
                  return (
                    <tr key={idx}>
                      <td className="text-nowrap">
                        {moment(row.meetingDatetime).format(
                          'MM/DD/YYYY h:mm A',
                        )}
                      </td>
                      <td className="text-center">
                        {directionIcon(meta.direction)}
                      </td>
                      <td className="text-nowrap">
                        {formatDuration(meta.durationMs)}
                      </td>
                      <td title={meta.summary}>
                        {meta.summary ? truncate(meta.summary) : '—'}
                      </td>
                      <td>
                        {variant ? (
                          <Badge bg={variant}>{meta.sentiment}</Badge>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => setTranscript(row)}
                        >
                          <FontAwesomeIcon
                            icon={faMagnifyingGlass}
                            fixedWidth
                          />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
      <TranscriptEditor
        show={transcript !== null}
        toggle={() => setTranscript(null)}
        claim={claim}
        transcript={transcript}
        questionnaireMatrix={questionnaireMatrix}
      />
    </>
  );
};

export default TranscriptsTab;
